import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
];

export default function QuestionnaireRun() {
  const { qid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [lang, setLang] = useState("en");
  const [answers, setAnswers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/questionnaires/${qid}?lang=${lang}`).then((r) => {
      setQ(r.data);
      setAnswers(new Array(r.data.items.length).fill(null));
    });
  }, [qid, lang]);

  if (!q) return <div className="animate-pulse text-[color:var(--mano-ink-soft)]">Loading…</div>;

  const total = q.items.length;
  const progress = ((idx + 1) / total) * 100;
  const currentAns = answers[idx];

  const setAnswer = (v) => {
    const next = [...answers];
    next[idx] = v;
    setAnswers(next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/questionnaires/${qid}/submit`, {
        questionnaire_id: qid,
        language: lang,
        answers: answers.map((a) => a ?? 0),
      });
      nav(`/report/${data.id}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const done = answers.every((a) => a !== null);

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="questionnaire-run">
      <Link to="/voicelab" className="inline-flex items-center gap-1 uppercase-label" data-testid="qr-back">
        <ChevronLeft size={14} /> Voice Lab
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="uppercase-label">Question {idx + 1} of {total}</span>
          <h1 className="font-display text-2xl mt-1">{q.name}</h1>
        </div>
        <select
          data-testid="qr-lang-select"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="h-10 px-3 rounded-full border border-[color:var(--mano-border)] bg-white"
        >
          {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>

      <div className="h-2 rounded-full bg-[color:var(--mano-border)] overflow-hidden">
        <div className="h-full bg-[color:var(--mano-primary)] transition-all" style={{ width: `${progress}%` }} />
      </div>

      <div key={idx} className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-8 fade-in-up">
        <div className="uppercase-label">Over the last 2 weeks…</div>
        <h2 className="font-display text-xl lg:text-2xl mt-3 leading-snug" data-testid="qr-item-text">
          {q.items[idx]}
        </h2>
        <div className="mt-6 space-y-2">
          {q.scale.map((s) => (
            <button
              key={s.value}
              data-testid={`qr-option-${s.value}`}
              onClick={() => setAnswer(s.value)}
              className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border text-left transition-colors ${
                currentAns === s.value
                  ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/8"
                  : "border-[color:var(--mano-border)] hover:bg-[color:var(--mano-border)]/30"
              }`}
            >
              <span className="font-medium">{s.label}</span>
              <span className={`h-6 w-6 rounded-full border-2 ${currentAns === s.value ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]" : "border-[color:var(--mano-border)]"}`} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="qr-prev-btn"
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0}
          className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border border-[color:var(--mano-border)] disabled:opacity-50"
        >
          <ChevronLeft size={14} /> Back
        </button>
        {idx < total - 1 ? (
          <button
            data-testid="qr-next-btn"
            onClick={() => setIdx(idx + 1)}
            disabled={currentAns === null}
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold disabled:opacity-50"
          >
            Next <ChevronRight size={14} />
          </button>
        ) : (
          <button
            data-testid="qr-submit-btn"
            onClick={submit}
            disabled={!done || busy}
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold disabled:opacity-50"
          >
            {busy ? "Submitting…" : "See my result"}
          </button>
        )}
      </div>
    </div>
  );
}
